import { Form, Popup } from 'devextreme-react';
import {
  ButtonItem,
  ButtonOptions,
  EmptyItem,
  GroupItem,
  Label,
  RequiredRule,
  SimpleItem,
} from 'devextreme-react/form';
import React, { useRef, useState } from 'react';
import { postApi, putApi } from '../../apiUtils';
import { useTranslation } from '../../contexts/translation';
import {
  MaintenanceCost,
  MaintenanceCostInputs,
  MaintenanceCostType,
} from '../../types/maintenanceCost.types';
import { API_URL } from '../../utils/apiUrl';
import { dateFieldOptionsWithSerialization } from '../../utils/dates.types';
import { TabFilesDatagrid } from '../fileManager/TabFilesDatagrid';

interface Props {
  isVisible: boolean;
  onClose: () => void;
  /** volá se po každém úspěšném zápisu, aby se přenačetl grid pod modálem */
  onSaved: () => void;
  assetId: number;
  costTypes?: MaintenanceCostType[];
  data?: MaintenanceCost | null;
  currency?: string;
  isReadOnly?: boolean;
}

/**
 * Přidání a editace nákladu na údržbu (#737).
 *
 * Přílohy jdou připojit i při zakládání - modál po prvním uložení POSTne náklad, z odpovědi
 * si vezme id a překlopí se do režimu editace. Sekce příloh se tím zpřístupní a modál zůstává
 * otevřený, takže to uživatel vnímá jako jeden formulář. Protože po prvním uložení už záznam
 * existuje, druhé tlačítko je "Zavřít", ne "Zrušit".
 */
/** Dnešek v lokálním čase - toISOString() by kolem půlnoci vracel jiný den (UTC). */
const today = () => {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

export default function MaintenanceCostModal({
  isVisible,
  onClose,
  onSaved,
  assetId,
  costTypes,
  data,
  currency,
  isReadOnly,
}: Props) {
  const { translate } = useTranslation();
  const transFormName = 'Asset.MaintenanceCost';
  const formRef = useRef<Form>(null);

  const [costId, setCostId] = useState<number>(data?.id ?? 0);
  const [saving, setSaving] = useState<boolean>(false);
  const [formData] = useState<MaintenanceCostInputs>(() => ({
    id: data?.id ?? 0,
    assetId,
    typeId: data?.typeId ?? null,
    supplier: data?.supplier ?? '',
    accountingDate: data?.accountingDate ?? today(),
    description: data?.description ?? '',
    amount: data?.amount ?? 0,
    reference: data?.reference ?? '',
    visible: data?.visible ?? true,
  }));

  // druhy nákladu - u editace doplníme i skrytý druh, ať o něj záznam nepřijde
  const costTypeItems = (() => {
    const items = costTypes ? [...costTypes] : [];
    if (data?.typeId && !items.some((t) => t.id === data.typeId))
      items.push({ id: data.typeId, name: data.typeName, sortOrder: 0, visible: false });
    return items;
  })();

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!formRef.current?.instance.validate().isValid) return;

    setSaving(true);
    if (costId > 0) {
      await putApi({
        url: API_URL.MAINTENANCE_COST_PUT,
        data: { ...formData, id: costId, assetId },
        callAfterSuccess: () => {
          onSaved();
          onClose();
        },
      });
    } else {
      await postApi<MaintenanceCost>({
        url: API_URL.MAINTENANCE_COST_POST,
        data: { ...formData, assetId },
        callAfterSuccess: (created: MaintenanceCost) => {
          // id z odpovědi je potřeba pro upload příloh (Attachment/Upload?entityId=...)
          if (created?.id) setCostId(created.id);
          onSaved();
        },
      });
    }
    setSaving(false);
  };

  return (
    <Popup
      visible={isVisible}
      dragEnabled={false}
      closeOnOutsideClick={false}
      onHiding={onClose}
      showTitle
      title={translate!(
        costId > 0 ? 'Edit maintenance cost' : 'New maintenance cost',
        transFormName,
      )}
      width={costId > 0 ? '80%' : 'auto'}
      height={'auto'}
    >
      <form onSubmit={handleSubmit}>
        <Form
          formData={formData}
          showColonAfterLabel
          showValidationSummary
          ref={formRef}
          readOnly={isReadOnly}
          colCount={2}
        >
          <GroupItem>
            <SimpleItem
              dataField="accountingDate"
              editorType="dxDateBox"
              editorOptions={{ ...dateFieldOptionsWithSerialization, width: 300 }}
            >
              <Label text={translate!('Accounting date', transFormName)} />
              <RequiredRule message={translate!('Accounting date is required', transFormName)} />
            </SimpleItem>
          </GroupItem>
          <GroupItem>
            <SimpleItem
              dataField="typeId"
              editorType="dxSelectBox"
              editorOptions={{
                items: costTypeItems,
                valueExpr: 'id',
                displayExpr: 'name',
                width: 300,
              }}
            >
              <Label text={translate!('Cost type', transFormName)} />
              <RequiredRule message={translate!('Cost type is required', transFormName)} />
            </SimpleItem>
          </GroupItem>
          <GroupItem>
            <SimpleItem
              dataField="supplier"
              editorType="dxTextBox"
              editorOptions={{ width: 300, maxLength: 200 }}
            >
              <Label text={translate!('Supplier', transFormName)} />
            </SimpleItem>
          </GroupItem>
          <GroupItem>
            <SimpleItem
              dataField="amount"
              editorType="dxNumberBox"
              editorOptions={{
                width: 300,
                format: { type: 'fixedPoint', precision: 2 },
                min: 0,
                showSpinButtons: false,
              }}
            >
              <Label
                text={`${translate!('Amount', transFormName)}${currency ? ` (${currency})` : ''}`}
              />
              <RequiredRule message={translate!('Amount is required', transFormName)} />
            </SimpleItem>
          </GroupItem>
          <GroupItem>
            <SimpleItem
              dataField="reference"
              editorType="dxTextBox"
              editorOptions={{ width: 300, maxLength: 100 }}
            >
              <Label text={translate!('Reference', transFormName)} />
            </SimpleItem>
          </GroupItem>
          <EmptyItem />
          <GroupItem colSpan={2}>
            <SimpleItem
              dataField="description"
              editorType="dxTextArea"
              editorOptions={{ height: 80, maxLength: 500 }}
            >
              <Label text={translate!('Description', transFormName)} />
            </SimpleItem>
          </GroupItem>
          <GroupItem>
            <SimpleItem dataField="visible" editorType="dxCheckBox">
              <Label text={translate!('Visible', transFormName)} />
            </SimpleItem>
          </GroupItem>
          <EmptyItem />

          <GroupItem colSpan={2} colCount={6}>
            <ButtonItem>
              <ButtonOptions
                width={'100%'}
                type={'normal'}
                text={translate!('Close', '')}
                onClick={onClose}
              />
            </ButtonItem>
            <ButtonItem>
              <ButtonOptions
                width={'100%'}
                type={'success'}
                text={translate!('Save', '')}
                disabled={saving || isReadOnly}
                useSubmitBehavior
              />
            </ButtonItem>
            <GroupItem colSpan={4}></GroupItem>
          </GroupItem>
        </Form>
      </form>

      {/* přílohy nákladu - dostupné až po prvním uložení, kdy existuje id */}
      {costId > 0 && (
        <div style={{ marginTop: 20 }}>
          <TabFilesDatagrid id={costId} entityName="MaintenanceCost" isReadonly={!!isReadOnly} />
        </div>
      )}
    </Popup>
  );
}
